import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";

export const createTypesensePartSearchAdapter = (mobileConfigs: any) => {
  try {
    if (!mobileConfigs?.typesense_host || !mobileConfigs?.typesense_api_key) {
      console.log("Typesense config missing!");
      return null;
    }
    return new TypesenseInstantSearchAdapter({
      server: {
        nodes: [
          {
            host: mobileConfigs.typesense_host,
            port: 443,
            protocol: "https",
          },
        ],
        apiKey: mobileConfigs.typesense_api_key,
      },
      additionalSearchParameters: {
        // Earlier `query_by` fields get higher implicit weight; prioritize part number over name at rank time.
        query_by: "partNumber,partName",
        num_typos: 0,
      },
    });
  } catch (error) {
    console.error("Failed to create Typesense adapter:", error);
    return null;
  }
};

export const mapTypesenseHitToBackendFormat = (part: any) => {
  try {
    console.log(part);
    const src = part?.document ? { ...part, ...part.document } : part;
    return {
      ...src,
      _id: src._id || src.partId || part._id,
    };
  } catch (error) {
    console.error("Failed to map Typesense hit:", error);
    return part;
  }
};

/** One page of part search for infinite queries: `hasMore` avoids relying on `items.length === limit` when merge/dedupe shrinks page 1. */
export type PartSearchPageResult = {
  items: any[];
  hasMore: boolean;
};

export const searchPartsInTypesense = async (
  typesenseAdapter: any,
  partNumber: string,
  page: number = 1,
  limit: number = 20,
): Promise<{ hits: any[]; hasMore: boolean }> => {
  try {
    const results = await typesenseAdapter.searchClient.search([
      {
        indexName: "partmasters",
        params: {
          query: partNumber.trim(),
          hitsPerPage: limit,
          page: page - 1,
          num_typos: 0,
        },
      },
    ] as any);
    console.log("results", results);

    const r = (results as any).results?.[0];
    const hits = r?.hits || [];
    const nbPages =
      typeof r?.nbPages === "number"
        ? r.nbPages
        : typeof r?.nbHits === "number" && limit > 0
          ? Math.ceil(r.nbHits / limit)
          : 0;
    const pageIndex = typeof r?.page === "number" ? r.page : page - 1;
    const hasMore = nbPages > 0 && pageIndex + 1 < nbPages;
    return { hits, hasMore };
  } catch (error) {
    console.error("Typesense search failed:", error);
    return { hits: [], hasMore: false };
  }
};

export const performPartSearchStrategy = async (
  partNumber: string,
  context: {
    typesenseAdapter: any;
    backendSearch: (partNumber: string) => Promise<any>;
    page?: number;
    limit?: number;
  },
): Promise<PartSearchPageResult> => {
  const trimmedPartNumber = partNumber.trim();
  if (trimmedPartNumber === "")
    return { items: [], hasMore: false };
  const page = context.page || 1;
  const limit = context.limit || 20;

  // Strategy 1: Typesense
  try {
    console.log(
      "Strategy 1: context.typesenseAdapter exists?",
      !!context.typesenseAdapter,
    );
    if (context.typesenseAdapter) {
      console.log("Calling searchPartsInTypesense for:", trimmedPartNumber);
      const { hits, hasMore: typesenseHasMore } = await searchPartsInTypesense(
        context.typesenseAdapter,
        trimmedPartNumber,
        page,
        limit,
      );
      if (hits && hits.length > 0) {
        const mapped = hits.map((hit: any) =>
          mapTypesenseHitToBackendFormat(hit),
        );
        return { items: mapped, hasMore: typesenseHasMore };
      }
    }
  } catch (error) {
    console.error("Strategy 1 (Typesense) failed:", error);
  }

  // Strategy 2: Backend Fallback (Only on page 1)
  if (page === 1) {
    try {
      const raw = await context.backendSearch(trimmedPartNumber);
      const rows = Array.isArray(raw) ? raw : [];
      const mapped = rows.map((row: any) => mapTypesenseHitToBackendFormat(row));
      return { items: mapped, hasMore: false };
    } catch (error) {
      console.error("Strategy 2 (Backend) failed:", error);
      return { items: [], hasMore: false };
    }
  }

  return { items: [], hasMore: false };
};
